import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bool, func, number, object, string, shape } from 'prop-types';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import LoadingTable from 'ps-components/AdvancedTable/LoadingTable/LoadingTable';
import Placeholder from '../Placeholder/Placeholder';
import './JobPricingDetails.scss';
import { FOCUS } from './JobPricingGrid/JobPricingGridCreate';
import GG_DEFAULTS from '../../../lib/genericGridDefaults';
import { getColumnIndex } from 'ps-components/AdvancedTable/GenericGrid/GenericGridHelper';
import { SlottedDetailsComment } from './JobPricingGrid/JobPricingCellRender';
import { jobMatchGridActions } from '../LandingPage/JobMatchDetail/JobMatchGridCreate';

import { COLLAPSE_KEY, CURRENT_JOB_KEY } from './../JobPricing/JobPricingContainer';
const ENTER_KEY_CODE = 13;
const ESC_KEY_CODE = 27;

JobPricingDetails.propTypes = {
  activeBenchmarkYear: number,
  aging: string,
  allowSorting: bool,
  benchmarkYear: number,
  displayIn: string,
  editPastYears: bool,
  getPricingDetailsForSlottedJob: func,
  getSlottedJobPageHref: func,
  getSurveyJobDetailsPageHref: func,
  hideBody: bool,
  hideFooter: bool,
  isOnSlotPage: bool,
  isPrint: bool,
  isSavingDataCutChangesComplete: bool,
  job: object,
  jobCodeKeyText: string,
  jobPricing: object,
  jobPricingGrid: object,
  jobPricingGridActions: object,
  loading: bool,
  masterAccountId: string,
  numericRounding: number,
  onDataCutClick: func,
  onDelete: func,
  onMeritMatchSummariesSave: func,
  onSurveyJobTitleClick: func,
  onSurveySave: func,
  options: object,
  overallToggleDetails: bool,
  percentRounding: number,
  readOnly: bool,
  recentDeletedMatchId: number,
  renderPopover: bool,
  search: string,
  selectedBenchmarkYear: number,
  selectedRowId: number,
  selectedViewPk: number,
  siteSettings: shape({
    showAgingFactor: bool,
    showWgtBy: bool,
    useParticipationMatch: bool,
    showGeoAdjustment: bool
  }),
  stickyHeader: bool,
  togglePopover: func,
  updateModifiedDate: func,
  useJobCodeKey: bool
};

JobPricingDetails.defaultProps = {
  stickyHeader: true,
  allowSorting: true,
  isPrint: false
};

function JobPricingDetails(props) {
  const dispatch = useDispatch();
  const [modifiedComment, setModifiedComment] = useState('');
  const [modifiedAdj, setModifiedAdj] = useState('');
  const [modifiedGeo, setModifiedGeo] = useState('');
  const [modifiedWgt, setModifiedWgt] = useState('');
  const [modifiedOverallComment, setModifiedOverallComment] = useState('');
  const [modifiedOverallAdj, setModifiedOverallAdj] = useState('');
  const [focusedInput, setFocusedInput] = useState('');
  const [shouldUseOverallToggleSetting, setShouldUseOverallToggleSetting] = useState(false);
  const currentFocusRef = useRef(null);

  // Track indexes below for efficient access
  let matchIdIndex,
    commentIndex,
    adjIndex,
    geoIndex,
    wgtIndex,
    overallCommentIndex,
    overallAdjIndex,
    participationIndex;
  props.jobPricingGrid.terms.forEach((term, index) => {
    if (
      !term ||
      (matchIdIndex &&
        commentIndex &&
        adjIndex &&
        geoIndex &&
        wgtIndex &&
        overallCommentIndex &&
        overallAdjIndex &&
        participationIndex)
    )
      return;
    if (!matchIdIndex && term.term === 'match_id_default') matchIdIndex = index;
    if (!commentIndex && term.term === 'data_adjustment_factor_reason_default') commentIndex = index;
    if (!wgtIndex && term.term === 'data_weighting_factor_default') wgtIndex = index;
    if (!geoIndex && term.term === 'geo_adj_factor_default') geoIndex = index;
    if (!adjIndex && term.term === 'data_adjustment_factor_default') adjIndex = index;
    if (!overallCommentIndex && term.term === 'benchmark_comment_default') overallCommentIndex = index;
    if (!overallAdjIndex && term.term === 'benchmark_adjustment_default') overallAdjIndex = index;
    if (!participationIndex && term.term === 'participation_p_default') participationIndex = index;
  });

  const currentSessionCollapse = window.sessionStorage
    .getItem(COLLAPSE_KEY)
    ?.split(',')
    .map(cutId => parseInt(cutId));
  const currentSessionJob = parseInt(window.sessionStorage.getItem('market_pricing_current_job'));
  if (isNaN(currentSessionJob) || currentSessionJob !== parseInt(props.job.id)) {
    setShouldUseOverallToggleSetting(true);
    window.sessionStorage.setItem(CURRENT_JOB_KEY, props.job.id);
  }

  const [matchesCollapsed, setMatchesCollapsed] = useState(currentSessionCollapse || []);
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const setMatches = props.overallToggleDetails ? props.jobPricingGrid?.data?.map(row => row[matchIdIndex]) : [];
    setMatchesCollapsed(setMatches);
    window.sessionStorage.setItem(COLLAPSE_KEY, setMatches);
  }, [props.overallToggleDetails, props.jobPricingGrid.data?.length]);

  // Detect if grid gets updated data
  useEffect(() => {
    if (shouldUseOverallToggleSetting && props.jobPricingGrid.data?.length > 0) {
      setShouldUseOverallToggleSetting(false);
      const setMatches = props.overallToggleDetails ? props.jobPricingGrid?.data?.map(row => row[matchIdIndex]) : [];
      setMatchesCollapsed(setMatches);
      window.sessionStorage.setItem(COLLAPSE_KEY, setMatches);
    }
  }, [props.jobPricingGrid.data]);

  // Initial call to get job pricing surveys
  useEffect(() => {
    if (props.benchmarkYear && props.displayIn) {
      props.updateGrid && props.updateGrid();
    }
  }, [props.benchmarkYear, props.displayIn, props.selectedViewPk, props.recentDeletedMatchId]);

  useEffect(() => {
    requestAnimationFrame(() => {
      currentFocusRef.current?.focus();
    });
  }, [currentFocusRef.current, focusedInput, props.jobPricingGrid?.currentCommentEditMatch]);

  useEffect(() => {
    if (props.isSavingDataCutChangesComplete === true) {
      props.updateGrid();
    }
  }, [props.isSavingDataCutChangesComplete]);

  // START: Functions to handle editable fields
  const onCommentChange = event => {
    setModifiedComment(event.target.value);
  };

  const onAdjChange = event => {
    setModifiedAdj(parseFloat(event.target.value));
  };

  const onGeoChange = event => {
    setModifiedGeo(parseFloat(event.target.value));
  };

  const onWgtChange = event => {
    setModifiedWgt(parseFloat(event.target.value));
  };

  const onOverallCommentChange = event => {
    setModifiedOverallComment(event.target.value);
  };

  const onOverallAdjChange = event => {
    let value = event.target.value || 1; // default adjustment value to 1
    setModifiedOverallAdj(parseFloat(value));
  };

  // END: Functions to handle editable fields

  // Called when editing fields for a individual match
  const saveMatch = (slice = true) => {
    const foundIndex = props.jobPricingGrid.data.findIndex(
      row =>
        row[matchIdIndex] === props.jobPricingGrid.currentAdjustmentEditMatch ||
        row[matchIdIndex] === props.jobPricingGrid.currentCommentEditMatch
    );
    const foundDataRow = props.jobPricingGrid.data[foundIndex];
    if (!foundDataRow) return;
    const updatedComment =
      modifiedComment === foundDataRow[commentIndex] || modifiedComment === ''
        ? foundDataRow[commentIndex]
        : slice
        ? modifiedComment.slice(0, -1)
        : modifiedComment; // Slice to take off the added \n when saving via 'Enter'

    const isAdjUpdated = modifiedAdj !== foundDataRow[adjIndex] && modifiedAdj !== '';
    const isGeoUpdated = modifiedGeo !== foundDataRow[geoIndex] && modifiedGeo !== '';
    const isWgtUpdated = modifiedWgt !== foundDataRow[wgtIndex] && modifiedWgt !== '';
    const updatedAdj = isAdjUpdated ? modifiedAdj : foundDataRow[adjIndex];
    const updatedGeo = isGeoUpdated ? modifiedGeo : foundDataRow[geoIndex];
    const updatedWgt = isWgtUpdated ? modifiedWgt : foundDataRow[wgtIndex];

    dispatch(jobMatchGridActions.setIsStale(props.job.id, true));
    // Updates the client before the backend finishes. Results in snappier experience.
    props.jobPricingGridActions.updateSpecificRowData(
      foundIndex,
      [commentIndex, adjIndex, geoIndex, wgtIndex],
      [updatedComment, updatedAdj || 0, updatedGeo || 0, updatedWgt || 0]
    );

    // Call to client to update changed fields.
    // WIP: Since the client updates first, should have a callback to update again when this request is finished.
    props
      .onSurveySave({
        matchId: foundDataRow[matchIdIndex],
        matchComments: updatedComment,
        surveyAdjustmentFactors: {
          adj: updatedAdj || 0,
          geo: updatedGeo || 0,
          wgt: updatedWgt || 0
        }
      })
      .then(() => {
        props.updateModifiedDate(props.job.id, props.benchmarkYear);
        if (isAdjUpdated || isGeoUpdated || isWgtUpdated) {
          props.updateGrid();
        }
      });
  };

  // Called when editing the overall adjustment or comment fields
  const saveOverall = (slice = true) => {
    const foundDataRow = props.jobPricingGrid.data[0];
    if (!foundDataRow) return;
    const isOverallAdjUpdated = modifiedOverallAdj !== foundDataRow[overallAdjIndex] && modifiedOverallAdj !== '';
    const updatedOverallComment =
      modifiedOverallComment === foundDataRow[overallCommentIndex] || modifiedOverallComment === ''
        ? foundDataRow[overallCommentIndex]
        : slice
        ? modifiedOverallComment.slice(0, -1)
        : modifiedOverallComment; // Slice to take off the added \n when saving via 'Enter'
    const updatedOverallAdj = isOverallAdjUpdated ? modifiedOverallAdj : foundDataRow[overallAdjIndex];
    props.jobPricingGridActions.updateSpecificRowData(
      0,
      [overallCommentIndex, overallAdjIndex],
      [updatedOverallComment, updatedOverallAdj || 0]
    );
    props.onMeritMatchSummariesSave(updatedOverallAdj || 0, updatedOverallComment).then(() => {
      props.updateModifiedDate(props.job.id, props.benchmarkYear);
      if (isOverallAdjUpdated) {
        props.updateGrid();
      }
    });
  };

  const clearModifiedStates = () => {
    setModifiedOverallAdj('');
    setModifiedComment('');
    setModifiedOverallComment('');
    setModifiedAdj('');
    setModifiedGeo('');
    setModifiedWgt('');
  };

  const handleKeyUpOnJob = event => {
    const keyCode = event.keyCode;
    if (keyCode === ESC_KEY_CODE) {
      props.jobPricingGridActions.cancelEditingAll();
      clearModifiedStates();
    } else if (keyCode === ENTER_KEY_CODE) {
      props.jobPricingGridActions.cancelEditingAll();
      saveMatch();
    }
  };

  const handleKeyUpOnOverall = event => {
    const keyCode = event.keyCode;
    if (keyCode === ESC_KEY_CODE) {
      props.jobPricingGridActions.cancelEditingAll();
      clearModifiedStates();
    } else if (keyCode === ENTER_KEY_CODE) {
      props.jobPricingGridActions.cancelEditingAll();
      saveOverall();
    }
  };

  const handleOnBlur = event => {
    const targetInputId = event.relatedTarget?.id;
    if (
      !targetInputId &&
      (focusedInput === 'comment-textarea-match' ||
        focusedInput === FOCUS.GEO ||
        focusedInput === FOCUS.ADJ ||
        focusedInput === FOCUS.WGT)
    ) {
      props.jobPricingGridActions.cancelEditingAll();
      if (modifiedAdj !== '' || modifiedGeo !== '' || modifiedWgt !== '') {
        saveMatch();
      } else if (modifiedComment !== '') {
        saveMatch(false);
      }
    }
  };

  const handleOnBlurOverall = event => {
    const targetInputId = event.relatedTarget?.id;
    if (!targetInputId && (focusedInput === 'comment-textarea-match' || focusedInput === 'overall-adjustment-match')) {
      props.jobPricingGridActions.cancelEditingAll();
      if (modifiedOverallComment !== '') {
        saveOverall(false);
      } else if (modifiedOverallAdj !== '') {
        saveOverall();
      }
    }
  };

  const handleMatchToggle = matchId => {
    let newMatchesCollapsed;
    if (matchesCollapsed.includes(matchId)) {
      newMatchesCollapsed = matchesCollapsed.filter(collapsedMatchId => collapsedMatchId !== matchId);
    } else {
      newMatchesCollapsed = matchesCollapsed.concat([matchId]);
    }
    setMatchesCollapsed(newMatchesCollapsed);
    window.sessionStorage.setItem(COLLAPSE_KEY, newMatchesCollapsed);
  };

  if (!props.hasJobDetails && !props.jobPricingGrid.processInitialLoad && !props.loading) {
    const { isAdHocJob, activeBenchmarkYear, selectedBenchmarkYear, isOnSlotPage } = props;

    let primaryText = 'No matches found',
      secondaryText = `Start ${isOnSlotPage ? 'slotting' : 'pricing'} this job by adding matches`;

    if (isAdHocJob) {
      primaryText = 'Start creating ad hoc pricing by adding matches';
      secondaryText = 'Save the pricing to create a new org job.';
    } else if (selectedBenchmarkYear < activeBenchmarkYear) {
      primaryText = (
        <span>
          This job is not priced or slotted in <b>{selectedBenchmarkYear}</b>
        </span>
      );
      secondaryText = 'Please switch to a different benchmark year';
    }
    return <Placeholder className="no-matches" primaryText={primaryText} secondaryText={secondaryText} />;
  }

  const hiddenColumns = props.jobPricingGrid.tableProps.hiddenColumns.filter(column => column !== 'delete-match');

  if (!props.siteSettings.showAgingFactor) {
    hiddenColumns.push('aging-column');
  }

  if (!props.siteSettings.showGeoAdjustment) {
    hiddenColumns.push('geo-column');
  }

  if (props.readOnly) {
    hiddenColumns.push('delete-match');
  }

  const isLoading = props.loading || props.jobPricingGrid.processInitialLoad;

  function getSubRowData(el) {
    const {
      surveysForSlottedMatches // object of org job ids
    } = props.jobPricingGrid;

    const orgJobId = el?.values?.org_job_id_default;

    const surveyData = surveysForSlottedMatches[orgJobId];
    if (surveyData) {
      return surveyData;
    }

    return null;
  }

  function getSubRows(rowData) {
    const {
      slottedDetailsLoading, // array of org job ids
      slottedExpandedSections, // object of org job ids
      surveysForSlottedMatches // object of org job ids
    } = props.jobPricingGrid;

    const orgJobIndex = getColumnIndex(props.jobPricingGrid, GG_DEFAULTS.ORG_JOB_ID);
    const orgJobId = rowData[orgJobIndex];

    if (
      orgJobId &&
      !slottedDetailsLoading.includes(orgJobId) &&
      slottedExpandedSections[orgJobId] &&
      surveysForSlottedMatches[orgJobId]
    ) {
      const surveyMatches = surveysForSlottedMatches[orgJobId];

      return surveyMatches?.data || [];
    }

    return [];
  }

  function getJobCurrency() {
    if (props.job?.currency) {
      return props.job.currency;
    }

    const { jobPricingGrid } = props;
    const currencyIndex = getColumnIndex(jobPricingGrid, GG_DEFAULTS.CURRENCY);

    if (jobPricingGrid?.data?.length > 0) {
      return jobPricingGrid.data[0][currencyIndex];
    }

    return undefined;
  }

  const getOptions = function(meta, idx) {
    return {
      ...props.options,
      activeBenchmarkYear: props.activeBenchmarkYear,
      adjustmentMatchId: props.jobPricingGrid.currentAdjustmentEditMatch,
      checkingParticipationIDs: props.jobPricingGrid.checkingParticipationIDs,
      clearModifiedStates,
      commentMatchId: props.jobPricingGrid.currentCommentEditMatch,
      currency: getJobCurrency(),
      currentFocusRef,
      editingOverallAdjustment: props.jobPricingGrid.editingOverallAdjustment,
      editingOverallComment: props.jobPricingGrid.editingOverallComment,
      editPastYears: props.editPastYears,
      focusedInput,
      getPricingDetailsForSlottedJob: props.getPricingDetailsForSlottedJob,
      getSlottedJobPageHref: props.getSlottedJobPageHref,
      getSurveyJobDetailsPageHref: props.getSurveyJobDetailsPageHref,
      handleKeyUpOnJob,
      handleKeyUpOnOverall,
      handleMatchToggle,
      handleOnBlur,
      handleOnBlurOverall,
      index: idx,
      jobPricingGridActions: props.jobPricingGridActions,
      matchesCollapsed,
      meta,
      numericRounding: props.numericRounding,
      onAdjChange,
      onCommentChange,
      onDataCutClick: props.onDataCutClick,
      onDelete: props.onDelete,
      onGeoChange,
      onOverallAdjChange,
      onOverallCommentChange,
      onSurveyJobTitleClick: props.onSurveyJobTitleClick,
      onWgtChange,
      orgJobId: props.job.id,
      overallBenchmarkAdjustment: props.jobPricingGrid.data[0][overallAdjIndex],
      overallBenchmarkComment: props.jobPricingGrid.data[0][overallCommentIndex],
      participationIndex,
      percentRounding: props.percentRounding,
      pricingType: props.jobPricingGrid.metadata.pricingType,
      readOnly: props.readOnly,
      renderPopover: props.renderPopover,
      saveMatch,
      saveOverall,
      selectedBenchmarkYear: props.selectedBenchmarkYear,
      selectedRowId: props.selectedRowId,
      setFocusedInput,
      setModifiedComment,
      setModifiedOverallComment,
      siteSettings: props.siteSettings,
      slottedDetailsLoading: props.jobPricingGrid.slottedDetailsLoading,
      slottedExpandedSections: props.jobPricingGrid.slottedExpandedSections,
      surveysForSlottedMatches: props.jobPricingGrid.surveysForSlottedMatches,
      togglePopover: props.togglePopover,
      uncheckingParticipationIDs: props.jobPricingGrid.uncheckingParticipationIDs,
      useJobCodeKey: props.useJobCodeKey,
      isPrint: props.isPrint
    };
  };

  return (
    <div className="generic-grid__container">
      {isLoading && !props.jobPricingGrid.data?.length ? (
        <LoadingTable className="pricing-details__loading-table" numberOfRows={3} includeHeader={true} />
      ) : (
        <AdvancedTable
          {...props.jobPricingGrid.tableProps}
          {...props.jobPricingGridActions}
          allowResize={true}
          allowSorting={props.allowSorting}
          commentComponent={(row, meta) => SlottedDetailsComment(row, getOptions(meta, row.id))}
          dataLoading={isLoading}
          defaultLeftColumns={props.jobPricingGrid.defaultLeftColumns}
          defaultRightColumns={props.jobPricingGrid.defaultRightColumns}
          getDataRow={(row, idx, meta) => getGenericRow(row, idx, getOptions(meta, idx))}
          getSubRowData={getSubRowData}
          getSubRows={getSubRows}
          hiddenColumns={hiddenColumns}
          hideBody={props.hideBody}
          hideCustom={false}
          hideFooter={props.hideFooter}
          isPrint={props.isPrint}
          loadingText={'Loading Job Data'}
          options={{
            pricingType: props.jobPricingGrid.metadata.pricingType,
            ...props.options
          }}
          stickyHeader={props.stickyHeader}
          useSelectAll={false}
        />
      )}
    </div>
  );
}

export default JobPricingDetails;
